import React, { useEffect, useState } from "react";
import { Loader2, Plus, Search, User } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useCurrency } from "@/hooks/useCurrency";
import { cn } from "@/lib/utils";

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[11px] text-slate-600 mb-1 block">{label}</Label>
      {children}
    </div>
  );
}

const EMPTY_NEW = {
  name: "",
  mobile: "",
  emirates_id: "",
  emirates_id_issued_on: "",
  emirates_id_expires_on: "",
  credit_limit: "",
};

function todayYmd() {
  return new Date().toISOString().slice(0, 10);
}

function addMonthsYmd(ymd, months) {
  const d = new Date(`${ymd}T00:00:00`);
  d.setMonth(d.getMonth() + months);
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

function eidExpiryStatus(expiresOn) {
  if (!expiresOn) {
    return { kind: "missing", message: "No Emirates ID expiry on file." };
  }
  const today = todayYmd();
  if (expiresOn < today) {
    return { kind: "expired", message: `Emirates ID expired on ${expiresOn}.` };
  }
  const withinMonth = expiresOn <= addMonthsYmd(today, 1);
  if (withinMonth) {
    return { kind: "soon", message: `Emirates ID expires within one month (${expiresOn}).` };
  }
  return { kind: "ok", message: "Emirates ID does not expire within one month." };
}

export default function PosCreditPopup({
  open,
  due,
  billCustomerId,
  customers,
  loading,
  search,
  onSearchChange,
  pending,
  onSelectCustomer,
  onChangeCustomer,
  selected,
  onCharge,
  onCreateCustomer,
  onClose,
}) {
  const { format, step: moneyStep, code: currencyCode } = useCurrency();
  const [adding, setAdding] = useState(false);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState(EMPTY_NEW);

  useEffect(() => {
    if (open) return;
    setAdding(false);
    setCreating(false);
    setForm(EMPTY_NEW);
  }, [open]);

  const dueAmt = Number(due) || 0;
  const outstanding = Number(selected?.balance || 0);
  const limitAmt = Number(selected?.credit ?? selected?.credit_limit ?? 0);
  const available = Math.max(0, limitAmt - outstanding);
  const overLimit = limitAmt > 0 && outstanding + dueAmt > limitAmt + 0.001;
  const eidStatus = selected ? eidExpiryStatus(selected.emirates_id_expires_on) : null;
  const expired = eidStatus?.kind === "expired";

  function setField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function openAddForm() {
    setForm({
      ...EMPTY_NEW,
      name: search.trim(),
    });
    setAdding(true);
  }

  async function submitNewCustomer() {
    const name = form.name.trim();
    if (!name) return;
    setCreating(true);
    try {
      const created = await onCreateCustomer({
        name,
        mobile: form.mobile.trim() || null,
        phone: form.mobile.trim() || null,
        emirates_id: form.emirates_id.trim() || null,
        emirates_id_issued_on: form.emirates_id_issued_on || null,
        emirates_id_expires_on: form.emirates_id_expires_on || null,
        credit_limit: Number(form.credit_limit) || 0,
        account_type: "Credit",
        subtype: "Regular",
        status: "Active",
      });
      if (created) {
        setAdding(false);
        setForm(EMPTY_NEW);
      }
    } finally {
      setCreating(false);
    }
  }

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[220] flex items-center justify-center bg-brand-brown/20 backdrop-blur-[2px] p-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="pos-credit-title"
      data-testid="pos-credit-dialog"
      onClick={onClose}
    >
      <div
        className="w-full max-w-lg max-h-[90vh] overflow-y-auto rounded-lg border bg-white shadow-2xl"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
      >
        <div className="border-b px-4 py-3">
          <h2 id="pos-credit-title" className="text-lg font-semibold text-slate-900">
            Credit sale
          </h2>
          <p className="text-xs text-slate-500 mt-0.5">
            {adding && !selected
              ? "Enter customer details for this credit sale."
              : "Select a customer or add a new one, then charge the bill to their account."}
          </p>
        </div>

        {!selected && adding ? (
          <div className="p-4 space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Field label="Name">
                <Input
                  value={form.name}
                  onChange={(e) => setField("name", e.target.value)}
                  className="h-9"
                  autoFocus
                  data-testid="pos-credit-new-name"
                />
              </Field>
              <Field label="Mobile">
                <Input
                  value={form.mobile}
                  onChange={(e) => setField("mobile", e.target.value)}
                  className="h-9"
                />
              </Field>
              <Field label="Emirates ID">
                <Input
                  value={form.emirates_id}
                  onChange={(e) => setField("emirates_id", e.target.value)}
                  className="h-9 font-mono"
                />
              </Field>
              <Field label={`Credit limit (${currencyCode})`}>
                <Input
                  type="number"
                  min="0"
                  step={moneyStep}
                  value={form.credit_limit}
                  onChange={(e) => setField("credit_limit", e.target.value)}
                  className="h-9 font-mono text-right"
                />
              </Field>
              <Field label="ID start date">
                <Input
                  type="date"
                  value={form.emirates_id_issued_on}
                  onChange={(e) => setField("emirates_id_issued_on", e.target.value)}
                  className="h-9"
                />
              </Field>
              <Field label="ID expiry date">
                <Input
                  type="date"
                  value={form.emirates_id_expires_on}
                  onChange={(e) => setField("emirates_id_expires_on", e.target.value)}
                  className="h-9"
                />
              </Field>
            </div>
          </div>
        ) : !selected ? (
          <div className="p-4 space-y-3">
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  value={search}
                  onChange={(e) => onSearchChange(e.target.value)}
                  placeholder="Search name, phone, Emirates ID…"
                  className="h-9 pl-8"
                  autoFocus
                  data-testid="pos-credit-customer-search"
                />
              </div>
              <Button
                type="button"
                variant="outline"
                className="h-9 shrink-0"
                onClick={openAddForm}
                data-testid="pos-credit-add-customer"
              >
                <Plus size={14} className="mr-1" /> Add new
              </Button>
            </div>
            <div className="max-h-72 overflow-y-auto border rounded-md">
              {loading && customers.length === 0 && (
                <p className="px-4 py-6 text-sm text-slate-500 text-center flex items-center justify-center gap-2">
                  <Loader2 size={14} className="animate-spin" /> Loading…
                </p>
              )}
              {customers.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  className={cn(
                    "w-full text-left px-4 py-3 text-sm hover:bg-slate-50 border-b last:border-b-0",
                    billCustomerId === c.id && "bg-blue-50",
                  )}
                  onClick={() => onSelectCustomer(c)}
                >
                  <div className="font-semibold text-slate-900">{c.name}</div>
                  <div className="text-[11px] text-slate-500 mt-0.5">
                    {[c.code, c.phone || c.mobile].filter(Boolean).join(" · ") || "—"}
                  </div>
                  <div className="text-[11px] text-slate-600 mt-0.5">
                    Limit {format(c.credit || 0)} · Outstanding {format(c.balance || 0)}
                  </div>
                </button>
              ))}
              {!loading && customers.length === 0 && (
                <p className="px-4 py-6 text-sm text-slate-500 text-center">No customers found</p>
              )}
            </div>
          </div>
        ) : (
          <div className="p-4 space-y-3">
            <div className="rounded-md border bg-slate-50 px-3 py-2 flex items-start justify-between gap-2">
              <div>
                <div className="flex items-center gap-1.5 font-semibold text-slate-900">
                  <User size={14} /> {selected.name}
                </div>
                <div className="text-[11px] text-slate-500 mt-0.5">
                  {[selected.code, selected.phone || selected.mobile].filter(Boolean).join(" · ")}
                </div>
              </div>
              <Button type="button" variant="outline" size="sm" className="h-7 text-[11px]" onClick={onChangeCustomer}>
                Change
              </Button>
            </div>

            <div
              className={cn(
                "text-[12px] rounded-md px-3 py-2 border",
                eidStatus.kind === "ok" && "text-emerald-800 bg-emerald-50 border-emerald-200",
                eidStatus.kind === "soon" && "text-amber-800 bg-amber-50 border-amber-200",
                (eidStatus.kind === "expired" || eidStatus.kind === "missing") && "text-red-700 bg-red-50 border-red-200",
              )}
              data-testid="pos-credit-eid-status"
            >
              {eidStatus.message}
            </div>

            <div className="grid grid-cols-3 gap-2 text-center text-[11px]">
              <div className="rounded-md border px-2 py-2">
                <div className="text-slate-500">Outstanding</div>
                <div className="font-mono font-semibold">{format(outstanding)}</div>
              </div>
              <div className="rounded-md border px-2 py-2">
                <div className="text-slate-500">This bill</div>
                <div className="font-mono font-semibold">{format(dueAmt)}</div>
              </div>
              <div className={cn("rounded-md border px-2 py-2", overLimit && "border-red-300 bg-red-50")}>
                <div className="text-slate-500">Available</div>
                <div className="font-mono font-semibold">{format(available)}</div>
              </div>
            </div>

            {overLimit && (
              <p className="text-[12px] text-amber-800 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                This bill would exceed the credit limit.
              </p>
            )}
          </div>
        )}

        <div className="flex flex-wrap justify-end gap-2 px-4 py-3 border-t">
          {adding && !selected ? (
            <>
              <Button
                variant="outline"
                disabled={creating}
                onClick={() => {
                  setAdding(false);
                  setForm(EMPTY_NEW);
                }}
              >
                Back
              </Button>
              <Button
                disabled={creating || !form.name.trim()}
                data-testid="pos-credit-create-customer"
                onClick={submitNewCustomer}
              >
                {creating ? <Loader2 size={14} className="animate-spin mr-1.5" /> : null}
                Save customer
              </Button>
            </>
          ) : (
            <Button variant="outline" onClick={onClose} disabled={pending}>
              Cancel
            </Button>
          )}
          {selected && (
            <Button
              className="bg-[#DC2626] hover:bg-red-700"
              disabled={pending || expired}
              data-testid="pos-credit-charge"
              onClick={() => onCharge(selected)}
            >
              {pending ? <Loader2 size={14} className="animate-spin mr-1.5" /> : null}
              Charge to account {aed(dueAmt)}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
